import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/cartcontext";
import { verifyCheckoutPayment } from "../services/orders";
import {
  clearCheckoutDraft,
  clearPendingCheckoutReference,
  readCheckoutDraft,
} from "../utils/checkoutStorage";

const CheckoutSuccess = () => {
  const { user, loading: authLoading } = useAuth();
  const { replaceCart, syncCartWithSupabase } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stage, setStage] = useState("verifying");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isActive = true;
    const reference = searchParams.get("reference")?.trim() || "";
    const draft = readCheckoutDraft();

    const finalizeCheckout = async () => {
      if (!reference) {
        throw new Error("Missing Paystack payment reference.");
      }

      if (draft?.pendingReference && draft.pendingReference !== reference) {
        throw new Error("Payment reference does not match the active checkout session.");
      }

      setStage("processing");
      const savedOrder = await verifyCheckoutPayment(reference);

      if (!isActive) {
        return;
      }
      replaceCart([]);
      clearPendingCheckoutReference();
      clearCheckoutDraft();

      syncCartWithSupabase().catch((syncError) => {
        console.error("Post-payment cart sync failed:", syncError);
      });

      if (!isActive) {
        return;
      }

      navigate("/orders", {
        replace: true,
        state: {
          latestOrder: savedOrder,
          paymentCompleted: true,
        },
      });
    };

    if (!authLoading && user) {
      finalizeCheckout().catch((error) => {
        console.error("Unable to finalize checkout:", error);

        if (!isActive) {
          return;
        }

        setStage("error");
        setErrorMessage(error.message || "Unable to verify payment.");
      });
    }

    return () => {
      isActive = false;
    };
  }, [authLoading, navigate, replaceCart, searchParams, syncCartWithSupabase, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen px-6 py-24">
        <div className="mx-auto max-w-3xl rounded-[2rem] bg-white p-10 text-center shadow-lg shadow-black/5">
          <p className="text-lg text-slate-700">Loading payment status...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: "/checkout-success", search: window.location.search } }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(254,249,195,0.9),_transparent_35%),linear-gradient(180deg,#fffdf7_0%,#ffffff_100%)] px-6 py-24">
      <div className="mx-auto max-w-3xl rounded-[2rem] bg-white p-10 text-center shadow-lg shadow-black/5">
        {stage === "error" ? (
          <>
            <p className="text-sm uppercase tracking-[0.28em] text-red-600">Payment issue</p>
            <h1 className="mt-3 text-4xl font-bold text-slate-900">We could not finalize this order</h1>
            <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/checkout-cancel"
                className="rounded-full border border-slate-300 px-5 py-3 font-semibold text-slate-900 transition hover:border-slate-400"
              >
                Return to checkout
              </Link>
              <Link
                to="/orders"
                className="rounded-full bg-slate-900 px-5 py-3 font-semibold text-white transition hover:bg-slate-800"
              >
                View orders
              </Link>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm uppercase tracking-[0.28em] text-amber-700">Checkout status</p>
            <h1 className="mt-3 text-4xl font-bold text-slate-900">
              {stage === "processing" ? "Processing order..." : "Verifying payment..."}
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              {stage === "processing"
                ? "Your payment was verified. We are finalizing your order and clearing your cart."
                : "We are confirming your Paystack transaction before creating the order."}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
